import React, { Fragment, memo, useCallback, useEffect, useState } from 'react'
import { Tooltip, Typography } from '@mui/material'
import tw from 'twin.macro'
import { useDispatch, useSelector } from 'react-redux'
import { colors } from 'src/theme'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { DeleteApiKey, FetchApiKeys } from 'src/constants/actionTypes'
import { handleApiError } from 'src/utils/errorHandlers'
import { FullScreenLoader } from '../shared/FullScreenLoader'
import { Button } from '../shared/Button'
import { CirclePlusMajor } from '@shopify/polaris-icons'
import { getCustomerApiKeys } from 'src/selectors/apiKey'
import { CustomerApiKey } from 'src/interfaces/apiKey'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { CreatePersonalAccessTokenModal } from './CreatePersonalAccessTokenModal'
import { SensitiveActionModal } from '../shared/SensitiveActionModal'
import { GSIcon } from '../shared/GSIcon'
import UnassignImage from 'src/assets/images/unassign.svg'
import { showToast } from 'src/utils/toast'
import dayjs from 'dayjs'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'

const PersonalAccessTokensSectionComponent = () => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const [isLoading, setIsLoading] = useState(true)
  const [deletingKey, setDeletingKey] = useState<CustomerApiKey | null>(null)

  const apiKeys = useSelector(getCustomerApiKeys)

  const { isOpen: isCreateOpen, open: openCreate, close: closeCreate } = useModalOpen()
  const { isOpen: isDeleteOpen, open: openDeleteModal, close: closeDeleteModal } = useModalOpen()

  const loadKeys = useCallback(async () => {
    setIsLoading(true)

    try {
      await createAsyncAction(dispatch, FetchApiKeys.request())
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [dispatch])

  useEffect(() => {
    loadKeys()
  }, [loadKeys])

  const handleOpenDelete = useCallback(
    (key: CustomerApiKey) => {
      setDeletingKey(key)
      openDeleteModal()
    },
    [openDeleteModal],
  )

  const handleCloseDelete = useCallback(() => {
    setDeletingKey(null)
    closeDeleteModal()
  }, [closeDeleteModal])

  const handleDelete = useCallback(async () => {
    if (!deletingKey) {
      return
    }

    try {
      await createAsyncAction(dispatch, DeleteApiKey.request({ keyId: deletingKey.id }))

      handleCloseDelete()

      showToast('API key revoked.')
    } catch (err) {
      handleApiError(err)
    }
  }, [deletingKey, dispatch, handleCloseDelete])

  if (isLoading) {
    return <FullScreenLoader />
  }

  return (
    <div css={tw`flex flex-col`}>
      <div css={tw`flex flex-col gap-2`}>
        <Typography variant="h2">API Keys</Typography>
        <Typography variant="body2" color={colors.SHADES_GREY_400}>
          API keys let your scripts and integrations authenticate with our API. Each key has
          admin-level access to your account — keep keys secure, anyone with a key can act on your
          account.
        </Typography>
      </div>

      <div css={tw`mt-4 flex flex-col gap-4`}>
        <Button
          color="light"
          size="small"
          className={tw`w-full justify-center rounded-lg py-2 pl-4 pr-3`}
          onClick={openCreate}
          rightIcon={<CirclePlusMajor height={20} width={20} fill={colors.PRIMARY_PURPLE_500} />}
        >
          Create new API key
        </Button>

        {apiKeys.length === 0 ? (
          <Typography variant="body2" color={colors.SHADES_GREY_400}>
            You don&apos;t have any API keys yet. Create one to use the GigSafe API from scripts or
            integrations.
          </Typography>
        ) : (
          <div css={tw`flex flex-col gap-2`}>
            {apiKeys.map((key) => (
              <div
                key={key.id}
                css={tw`flex h-[72px] flex-row items-center justify-between rounded-2xl border border-solid border-[#EDEDED] bg-white px-4 py-0`}
              >
                <div css={tw`flex min-w-0 flex-1 flex-row items-center gap-4`}>
                  <span
                    css={tw`inline-flex h-5 w-5 flex-shrink-0 items-center justify-center [&_svg]:h-5 [&_svg]:w-5 [&_svg]:flex-shrink-0`}
                    aria-hidden
                  >
                    <GSIcon iconName="gs_unlocked" width={20} height={20} />
                  </span>
                  <div css={tw`flex min-w-0 flex-col justify-center`}>
                    <div css={tw`flex flex-row flex-wrap items-baseline gap-2`}>
                      <Typography
                        variant="h6"
                        css={tw`min-w-0 max-w-full truncate`}
                        title={key.name}
                      >
                        {key.name}
                      </Typography>
                      {key.revokedAt && (
                        <Typography variant="body2" color={colors.SHADES_GREY_400}>
                          (revoked)
                        </Typography>
                      )}
                    </div>
                    <div css={tw`flex flex-row items-center gap-1`}>
                      <Typography variant="body2" color={colors.SHADES_GREY_500}>
                        {dayjs(key.createdAt).format('MM/DD/YY • h:mm A')}
                      </Typography>
                      {key.createdBy?.email ? (
                        <Tooltip
                          arrow
                          color="black"
                          placement="top"
                          title={
                            <Fragment>
                              <Typography
                                css={tw`mb-2`}
                                variant="h6"
                                color={colors.SHADES_GREY_200}
                              >
                                Created by
                              </Typography>
                              <Typography variant="body2" color={colors.SHADES_GREY_300}>
                                {key.createdBy.email}
                              </Typography>
                            </Fragment>
                          }
                        >
                          <span css={tw`inline-flex cursor-default`}>
                            <GSIcon iconName="gs_icon_people_circle" width={20} height={20} />
                          </span>
                        </Tooltip>
                      ) : null}
                    </div>
                  </div>
                </div>
                {!key.revokedAt && hasPermission(UserPermission.ApiKeysRevoke) && (
                  <Tooltip
                    arrow
                    color="black"
                    placement="top"
                    title={
                      <Fragment>
                        <Typography css={tw`mb-2`} variant="h6" color={colors.SHADES_GREY_200}>
                          Revoke API key
                        </Typography>
                        <Typography variant="body2" color={colors.SHADES_GREY_300}>
                          Permanently remove this key. API access using it will stop immediately.
                        </Typography>
                      </Fragment>
                    }
                  >
                    <span css={tw`ml-2 inline-flex`}>
                      <Button
                        color="white"
                        size="small"
                        className={tw`rounded-lg border border-solid border-[#EDEDED] p-2`}
                        aria-label={`Revoke ${key.name}`}
                        iconOnly
                        leftIcon={<GSIcon iconName="gs_trash_can" width={20} height={20} />}
                        onClick={() => handleOpenDelete(key)}
                      />
                    </span>
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <CreatePersonalAccessTokenModal open={isCreateOpen} onClose={closeCreate} />

      <SensitiveActionModal
        isOpen={isDeleteOpen}
        title="Revoke API Key?"
        text="API access using this key will stop immediately. This action cannot be undone."
        submitLabel="Revoke"
        color="red"
        className={tw`w-[330px] min-w-[330px]`}
        image={<img src={UnassignImage} alt="" css={tw`h-[164px] w-[210px] object-contain`} />}
        onClose={handleCloseDelete}
        onSubmit={handleDelete}
      />
    </div>
  )
}

export const PersonalAccessTokensSection = memo(PersonalAccessTokensSectionComponent)
